import React, { useDeferredValue, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  FaBuilding,
  FaCheck,
  FaCopy,
  FaFilter,
  FaLink,
  FaMapMarkerAlt,
  FaProjectDiagram,
  FaSearch,
  FaSitemap,
  FaSlidersH,
  FaUsers,
} from "react-icons/fa";
import { setActivePage } from "../store/activePage";
import { AxiosGetMethod } from "../helpers/customAxios";
import handleCopy from "../helpers/copyToClipboard";

const LEVEL_META = {
  1: {
    label: "Level 1",
    badge: "bg-teal-100 text-teal-800 border-teal-200",
    accent: "border-teal-200",
    chip: "bg-teal-50 text-teal-700",
  },
  2: {
    label: "Level 2",
    badge: "bg-sky-100 text-sky-800 border-sky-200",
    accent: "border-sky-200",
    chip: "bg-sky-50 text-sky-700",
  },
  3: {
    label: "Level 3",
    badge: "bg-amber-100 text-amber-800 border-amber-200",
    accent: "border-amber-200",
    chip: "bg-amber-50 text-amber-700",
  },
};

const TAMIL_CHARACTER_REGEX = /[\u0B80-\u0BFF]/g;

function removeTamilCharacters(value) {
  return String(value || "").replace(TAMIL_CHARACTER_REGEX, "");
}

function flattenReferralTree(nodes = [], parentPath = []) {
  return nodes.reduce((acc, node) => {
    const label = node.organisation_name || "Unnamed Organisation";
    const currentPath = [...parentPath, label];
    const children = Array.isArray(node.children) ? node.children : [];

    acc.push({
      ...node,
      children,
      childCount: children.length,
      pathLabels: currentPath,
      pathText: currentPath.join(" / "),
      parentLabel:
        parentPath.length > 0 ? parentPath[parentPath.length - 1] : node.parent_org_name || "",
    });

    if (children.length > 0) {
      acc.push(...flattenReferralTree(children, currentPath));
    }

    return acc;
  }, []);
}

function StatCard({ label, value, hint, icon }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
            {label}
          </p>
          <p className="mt-2 font-mono text-2xl font-black tracking-tight text-slate-900">
            {value}
          </p>
          {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          {icon}
        </div>
      </div>
    </div>
  );
}

function ReferralLinkBox({ code, copied, onCopy }) {
  const normalizedCode = String(code || "").toUpperCase();
  const fullLink = `https://enterprise.digitalmoitech.com/register/${normalizedCode}`;
  const shareText = `Join Digital Moi Tech using my referral link: ${fullLink}`;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 text-slate-800">
        <FaLink className="text-sm text-teal-700" />
        <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
          Your Referral Link
        </p>
      </div>

      <div className="mt-3 flex flex-col gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 md:flex-row md:items-center">
        <p className="min-w-0 flex-1 truncate font-mono text-sm text-slate-700">
          {fullLink}
        </p>
        <button
          type="button"
          onClick={onCopy}
          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold transition ${
            copied
              ? "bg-emerald-600 text-white"
              : "border border-teal-200 bg-teal-50 text-teal-700 hover:bg-teal-100"
          }`}
        >
          {copied ? <FaCheck className="text-xs" /> : <FaCopy className="text-xs" />}
          {copied ? "Copied" : "Copy Link"}
        </button>
      </div>

      <div className="mt-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-3">
        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
          Share Preview
        </p>
        <p className="mt-1 text-xs leading-5 text-slate-600">{shareText}</p>
      </div>
    </div>
  );
}

function LevelSummary({ levelCounts = {} }) {
  const items = [1, 2, 3].map((level) => ({
    level,
    count: levelCounts[level] || 0,
    meta: LEVEL_META[level],
  }));

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
            Visible Referral Levels
          </p>
          <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">
            Level 1 to Level 3 only
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Level 4 and deeper referrals are intentionally hidden from this dashboard.
          </p>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-slate-200">
        {items.map(({ level, count, meta }) => (
          <div
            key={level}
            className={`flex items-center justify-between gap-3 px-4 py-3 ${
              level !== 3 ? "border-b border-slate-200" : ""
            }`}
          >
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide ${meta.badge}`}
                >
                  {meta.label}
                </span>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                {level === 1 && "Direct referrals under your organisation"}
                {level === 2 && "Referrals added under level 1 organisations"}
                {level === 3 && "Referrals added under level 2 organisations"}
              </p>
            </div>
            <span className="shrink-0 font-mono text-2xl font-black text-slate-900">
              {count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilterChip({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-2 text-xs font-bold transition ${
        active
          ? "border-slate-900 bg-slate-900 text-white"
          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function ReferralNodeCard({ node }) {
  const meta = LEVEL_META[node.level] || LEVEL_META[3];

  return (
    <div
      className={`rounded-2xl border bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${meta.accent}`}
    >
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${meta.badge}`}
            >
              {meta.label}
            </span>
            {node.parentLabel ? (
              <span
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${meta.chip}`}
              >
                Reports under {node.parentLabel}
              </span>
            ) : null}
            {node.referral_code ? (
              <span className="rounded-full bg-slate-100 px-2.5 py-1 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                {node.referral_code}
              </span>
            ) : null}
          </div>

          <div className="mt-2 flex items-start gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <FaBuilding />
            </div>
            <div className="min-w-0">
              <h3 className="truncate text-base font-black tracking-tight text-slate-900">
                {node.organisation_name || "Unnamed Organisation"}
              </h3>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <FaMapMarkerAlt className="text-xs text-slate-400" />
                  {node.location || "Location not available"}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-3 rounded-xl bg-slate-50 px-3 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Branch path
            </p>
            <p className="mt-1 text-sm leading-6 text-slate-700">{node.pathText}</p>
          </div>
        </div>

<div className="grid shrink-0 grid-cols-2 gap-2 xl:w-56">
           <div className="rounded-xl bg-slate-100 px-3 py-3">
             <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
               Direct refs
             </p>
             <p className="mt-1 font-mono text-xl font-black tracking-tight text-slate-900">
               {node.direct_referral_count || 0}
             </p>
           </div>
           <div className="rounded-xl bg-slate-100 px-3 py-3">
             <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
               Paid events
             </p>
             <p className="mt-1 font-mono text-xl font-black tracking-tight text-slate-900">
               {node.event_count || 0}
             </p>
           </div>
         </div>
      </div>
    </div>
  );
}

function ReferralHierarchySection({ loading, error, tree = [] }) {
  const [searchText, setSearchText] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("all");
  const [sortMode, setSortMode] = useState("branch");
  const deferredSearchText = useDeferredValue(searchText);
  const flattenedTree = flattenReferralTree(tree);
  const normalizedSearch = deferredSearchText.trim().toLowerCase();

  const filteredNodes = flattenedTree
    .filter((node) => {
      const levelMatch =
        selectedLevel === "all" ? true : Number(selectedLevel) === Number(node.level);

      if (!levelMatch) return false;
      if (!normalizedSearch) return true;

      const searchPool = [
        node.organisation_name,
        node.location,
        node.parentLabel,
        node.pathText,
        node.referral_code,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchPool.includes(normalizedSearch);
    })
    .sort((left, right) => {
      if (sortMode === "network") {
        return (
          (right.direct_referral_count || 0) - (left.direct_referral_count || 0) ||
          (right.childCount || 0) - (left.childCount || 0)
        );
      }

      if (sortMode === "name") {
        return String(left.organisation_name || "").localeCompare(
          String(right.organisation_name || ""),
        );
      }

      return (
        Number(left.level || 0) - Number(right.level || 0) ||
        String(left.pathText || "").localeCompare(String(right.pathText || ""))
      );
    });

  const groupedNodes = [1, 2, 3]
    .map((level) => ({
      level,
      meta: LEVEL_META[level],
      items: filteredNodes.filter((node) => Number(node.level) === level),
    }))
    .filter((group) => group.items.length > 0);

  const totalMatches = filteredNodes.length;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div>
          <div className="flex items-center gap-2 text-slate-800">
            <FaSitemap className="text-sm text-teal-700" />
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
              Referral Network
            </p>
          </div>
          <h2 className="mt-1 text-lg font-black tracking-tight text-slate-900">
            Organisation tree under your dashboard
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Large networks are easier to scan here with search, level buckets, and
            a flatter branch explorer.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:w-[22rem]">
          <div className="rounded-xl bg-slate-100 px-3 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Visible cards
            </p>
            <p className="mt-1 font-mono text-xl font-black tracking-tight text-slate-900">
              {totalMatches}
            </p>
          </div>
          <div className="rounded-xl bg-slate-100 px-3 py-3">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              Root branches
            </p>
            <p className="mt-1 font-mono text-xl font-black tracking-tight text-slate-900">
              {tree.length}
            </p>
          </div>
          <div className="col-span-2 rounded-xl bg-slate-100 px-3 py-3 sm:col-span-1">
            <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
              View mode
            </p>
            <p className="mt-1 text-sm font-bold text-slate-700">Grouped explorer</p>
          </div>
        </div>
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm font-semibold text-slate-500">
            Loading referral hierarchy...
          </div>
        ) : error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-10 text-center text-sm font-semibold text-red-700">
            {error}
          </div>
        ) : tree.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm font-semibold text-slate-500">
            No visible referrals found under your organisation yet.
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div className="flex-1">
                  <label className="text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    Search by organisation, location, parent, or referral code
                  </label>
                  <div className="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <FaSearch className="shrink-0 text-sm text-slate-400" />
                    <input
                      type="text"
                      value={searchText}
                      onChange={(event) =>
                        setSearchText(removeTamilCharacters(event.target.value))
                      }
                      onPaste={(event) => {
                        const pastedText = event.clipboardData?.getData("text") || "";
                        const sanitizedText = removeTamilCharacters(pastedText);

                        if (pastedText === sanitizedText) return;

                        event.preventDefault();
                        const input = event.currentTarget;
                        const selectionStart = input.selectionStart ?? searchText.length;
                        const selectionEnd = input.selectionEnd ?? searchText.length;
                        const nextValue =
                          searchText.slice(0, selectionStart) +
                          sanitizedText +
                          searchText.slice(selectionEnd);

                        setSearchText(nextValue);
                      }}
                      placeholder="Try Chennai, Level 2 parent name, or referral code"
                      className="no-translate w-full border-0 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
                    />
                  </div>
                </div>

                <div className="xl:w-72">
                  <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    <FaSlidersH className="text-[10px]" />
                    Sort cards
                  </p>
                  <div className="mt-2 grid grid-cols-3 gap-2">
                    <FilterChip
                      active={sortMode === "branch"}
                      onClick={() => setSortMode("branch")}
                    >
                      Branch
                    </FilterChip>
                    <FilterChip
                      active={sortMode === "network"}
                      onClick={() => setSortMode("network")}
                    >
                      Network
                    </FilterChip>
                    <FilterChip
                      active={sortMode === "name"}
                      onClick={() => setSortMode("name")}
                    >
                      Name
                    </FilterChip>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-500">
                    <FaFilter className="text-[10px]" />
                    Filter levels
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <FilterChip
                      active={selectedLevel === "all"}
                      onClick={() => setSelectedLevel("all")}
                    >
                      All Levels
                    </FilterChip>
                    {[1, 2, 3].map((level) => (
                      <FilterChip
                        key={level}
                        active={selectedLevel === String(level)}
                        onClick={() => setSelectedLevel(String(level))}
                      >
                        {LEVEL_META[level].label}
                      </FilterChip>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">
                  Focus on one level when the list grows, then use branch path to trace
                  where that organisation sits.
                </div>
              </div>
            </div>

            {groupedNodes.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm font-semibold text-slate-500">
                No organisations match your current search or level filter.
              </div>
            ) : (
              <div className="space-y-5">
                {groupedNodes.map(({ level, meta, items }) => (
                  <section key={level} className="space-y-3">
                    <div className="flex flex-col gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] ${meta.badge}`}
                          >
                            {meta.label}
                          </span>
                          <span className="text-sm font-semibold text-slate-500">
                            {items.length} organisations
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-slate-500">
                          {level === 1 &&
                            "Direct organisations under your dashboard, shown first for quick follow-up."}
                          {level === 2 &&
                            "Second-line branches grouped together so you can review expansion patterns faster."}
                          {level === 3 &&
                            "Deepest visible branches collected in one place to reduce long nested scrolling."}
                        </p>
                      </div>
                    </div>

                    <div className="grid gap-3">
                      {items.map((node) => (
                        <ReferralNodeCard
                          key={`${node.level}-${node.org_id}-${node.referral_code || "ref"}`}
                          node={node}
                        />
                      ))}
                    </div>
                  </section>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function OperatorRewardsandReferrals() {
  const dispatch = useDispatch();
  const [copied, setCopied] = useState(false);
  const [referralCode, setReferralCode] = useState({ code: "", count: "0" });
  const [hierarchy, setHierarchy] = useState(null);
  const [loadingHierarchy, setLoadingHierarchy] = useState(true);
  const [hierarchyError, setHierarchyError] = useState("");

  const handleCopyClick = async () => {
    const refCode = String(referralCode.code || "").toUpperCase();
    const shareText = `Join Digital Moi Tech using my referral link: https://enterprise.digitalmoitech.com/register/${refCode}`;
    const isCopied = await handleCopy(shareText);
    if (!isCopied) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    dispatch(
      setActivePage({
        headerName: "Rewards and Referrals",
        activeMenu: "rewardsPage",
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    const code = localStorage.getItem("org-refferral-code") || "";
    const count = localStorage.getItem("org-refferral-count") || "0";
    setReferralCode({ code, count });
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchHierarchy = async () => {
      setLoadingHierarchy(true);
      setHierarchyError("");

      const res = await AxiosGetMethod("org/referral-hierarchy");
      if (!isMounted) return;

      if (res?.status === 200) {
        setHierarchy(res.data?.data || null);
      } else {
        setHierarchy(null);
        setHierarchyError(
          res?.data?.message || "Unable to load referral hierarchy right now.",
        );
      }

      setLoadingHierarchy(false);
    };

    fetchHierarchy();

    return () => {
      isMounted = false;
    };
  }, []);

  const levelCounts = hierarchy?.level_counts || {};
  const visibleReferralCount =
    hierarchy?.visible_referral_count ?? referralCode.count ?? "0";
  const directReferralCount = hierarchy?.level_counts?.[1] ?? referralCode.count ?? "0";
  const tree = hierarchy?.tree || [];
  const rootName =
    hierarchy?.root?.organisation_name ||
    localStorage.getItem("org-name") ||
    "Your Organisation";

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-6xl space-y-4">
        <div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm">
          <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center gap-2 rounded-full bg-teal-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-teal-700">
                <FaProjectDiagram className="text-[10px]" />
                Referral View
              </div>
              <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">
                Rewards & Referrals
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Showing organisations under {rootName} for levels 1 to 3.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
                Visible: Level 1-3
              </div>
              <div className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700">
                Hidden: Level 4+
              </div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 md:grid-cols-3">
          <StatCard
            label="Referral Code"
            value={String(referralCode.code || "-").toUpperCase() || "-"}
            hint="Share this to onboard organisations."
            icon={<FaLink />}
          />
          <StatCard
            label="Visible Referrals"
            value={visibleReferralCount}
            hint="All organisations shown across levels 1 to 3."
            icon={<FaUsers />}
          />
          <StatCard
            label="Level 1"
            value={directReferralCount}
            hint="Direct organisations under your dashboard."
            icon={<FaSitemap />}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-[1.15fr_0.85fr]">
          <ReferralLinkBox
            code={referralCode.code}
            copied={copied}
            onCopy={handleCopyClick}
          />
          <LevelSummary levelCounts={levelCounts} />
        </div>

        <ReferralHierarchySection
          loading={loadingHierarchy}
          error={hierarchyError}
          tree={tree}
        />
      </div>
    </div>
  );
}

export default OperatorRewardsandReferrals;
